import { useState } from 'react';
import { User } from '../../../@types/users';
import { ActionButton } from '../../../components/action-button';
import { CheckboxInput } from '../../../components/checkbox-input';
import { Modal } from '../../../components/modal';
import { PERMISSION } from '../../../consts/permissions';
import { useAuthPermissions } from '../../../hooks/auth';
import { useUpdateUser } from '../../../queries/users';
import {
  ButtonsRow,
  CheckboxRow,
  CustomInput,
  CustomSelect,
  ErrorMessage,
  OptionalFieldsContainer,
  OptionalToggle,
  RemoveCredentialsButton,
  SelectLabel,
  SelectWrapper,
} from './style';

const SYSTEM_ROLES = [
  { id: 5, name: 'מתנדב' },
  { id: 4, name: 'אחראי חלוקה' },
  { id: 3, name: 'אחראי כתובות' },
  { id: 2, name: "צ'יף" },
];

type EditUserModalProps = {
  user: User;
  onUserUpdated?: (user: User) => void;
  onCancel: () => void;
};

export const EditUserModal = ({
  user,
  onUserUpdated,
  onCancel,
}: EditUserModalProps) => {
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [isDriver, setIsDriver] = useState(Boolean(user.isDriver));
  const [systemRoleId, setSystemRoleId] = useState<number>(
    Number(user.role?.id) || 5,
  );

  const { hasPermissions } = useAuthPermissions();
  const canSetRole = hasPermissions([PERMISSION.SET_USER_ROLE]);
  const canSetUser = hasPermissions([PERMISSION.SET_USER_CREDENTIALS]);
  const isSystemAdmin = Number(user.role?.id) === 1;

  const [hasCredentials, setHasCredentials] = useState(Boolean(user.username));
  const [username, setUsername] = useState(user.username || '');
  const [password, setPassword] = useState('');

  const [errorMessage, setErrorMessage] = useState('');

  const { mutate: mutateUpdateUser, isLoading: isUpdating } = useUpdateUser();

  const handleSave = () => {
    setErrorMessage('');

    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();

    if (!trimmedFirstName) {
      setErrorMessage('יש להזין שם פרטי');

      return;
    }

    if (!trimmedLastName) {
      setErrorMessage('יש להזין שם משפחה');

      return;
    }

    if (canSetUser && hasCredentials) {
      if (!trimmedUsername) {
        setErrorMessage('יש להזין שם משתמש');

        return;
      }

      // If user had no username previously and is now setting one, password is required
      if (!user.username && !trimmedPassword) {
        setErrorMessage('יש להזין סיסמה למשתמש חדש');

        return;
      }
    }

    mutateUpdateUser(
      {
        id: user.id,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        isDriver,
        systemRoleId: canSetRole && !isSystemAdmin ? systemRoleId : undefined,
        username: canSetUser ? (hasCredentials ? trimmedUsername : null) : undefined,
        password: canSetUser && hasCredentials && trimmedPassword ? trimmedPassword : undefined,
      },
      {
        onSuccess: (updatedUser) => {
          if (onUserUpdated) {
            onUserUpdated(updatedUser);
          }
          onCancel();
        },
        onError: (error) => {
          const serverMessage = error.response?.data?.errorMessage;
          setErrorMessage(serverMessage || 'אירעה שגיאה בעדכון המשתמש');
        },
      },
    );
  };

  return (
    <Modal title="עריכת משתמש" isVisible onClose={onCancel}>
      <form
        onSubmit={(event) => {
          event.stopPropagation();
          event.preventDefault();
          handleSave();
        }}
      >
        <CustomInput
          label="שם פרטי"
          value={firstName}
          onChange={(e) => setFirstName(e.currentTarget.value)}
          placeholder="לדוגמה: ישראל"
          autoFocus
        />

        <CustomInput
          label="שם משפחה"
          value={lastName}
          onChange={(e) => setLastName(e.currentTarget.value)}
          placeholder="לדוגמה: ישראלי"
        />

        <CheckboxRow>
          <CheckboxInput
            label="נהג"
            isChecked={isDriver}
            onToggleCheck={() => setIsDriver(!isDriver)}
          />
        </CheckboxRow>

        {canSetRole && (
          <SelectWrapper>
            <SelectLabel>תפקיד</SelectLabel>
            <CustomSelect
              value={isSystemAdmin ? 1 : systemRoleId}
              disabled={isSystemAdmin}
              onChange={(e) => setSystemRoleId(Number(e.currentTarget.value))}
            >
              {isSystemAdmin && (
                <option value={1} disabled>
                  מנהל מערכת
                </option>
              )}
              {SYSTEM_ROLES.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </CustomSelect>
          </SelectWrapper>
        )}

        {canSetUser && (
          <div>
            {hasCredentials ? (
              <OptionalFieldsContainer>
                <CustomInput
                  label="שם משתמש"
                  value={username}
                  onChange={(e) => setUsername(e.currentTarget.value)}
                  placeholder="למשתמש בעל גישה למערכת"
                />
                <CustomInput
                  label={user.username ? 'סיסמה חדשה (אופציונלי)' : 'סיסמה'}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  placeholder={user.username ? 'השאר ריק כדי לא לשנות' : ''}
                />
                <RemoveCredentialsButton
                  type="button"
                  onClick={() => {
                    setHasCredentials(false);
                    setUsername('');
                    setPassword('');
                  }}
                >
                  הסרת פרטי התחברות למערכת
                </RemoveCredentialsButton>
              </OptionalFieldsContainer>
            ) : (
              <OptionalToggle
                type="button"
                onClick={() => setHasCredentials(true)}
              >
                + הוספת פרטי התחברות (אופציונלי)
              </OptionalToggle>
            )}
          </div>
        )}

        {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

        <ButtonsRow>
          <ActionButton
            title={isUpdating ? 'שומר...' : 'שמירה'}
            type="submit"
            disabled={isUpdating}
          />
          <ActionButton title="ביטול" onClick={onCancel} kind="secondary" disabled={isUpdating} />
        </ButtonsRow>
      </form>
    </Modal>
  );
};
